import { Row, Col } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { languages } from "../../../utils/helpers/constants";
import { getTranslations } from "../../../utils/helpers/translations";
import FieldBuilder from "../../form-components/field-builder";
import MainForm from "../../form-components/main-form";
import Spin from "../../general/antd/spin";
import GeneralNotificationContext from "../../../context/general-notification/context";
import GeneralNotificationContextProvider from "../../../context/general-notification/provider";
import { IUpdateGeneralNotification } from "../../../models/general-notification/request";
import {getNotificationType, NotificationType} from "../../../models/general-notification/enum";
import EndPoints from "../../../services/end-points";
import {ICampaign} from "../../../models/campaign/response";
import {TopicType} from "../../../models/notification/enum";
import { ICharity } from "../../../models/charity/response";
import { IOrphan } from "../../../models/orphan/response";

const FormContent = () => {
  const { t } = useTranslation();
  const formContext = useFormContext();
  const typeValue = formContext.watch("type");

  const { details, actions, loading } = useContext(GeneralNotificationContext);

  // States for different entity types
  const [campaigns, setCampaigns] = useState<ICampaign[]>([]);
  const [charities, setCharities] = useState<ICharity[]>([]);
  const [orphans, setOrphans] = useState<IOrphan[]>([]);
  
  // Loading states
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [charityLoading, setCharityLoading] = useState(false);
  const [orphanLoading, setOrphanLoading] = useState(false);

  // Helper function to get loading state based on type
  const getLoadingStateByType = (type: number) => {
    switch (type) {
      case NotificationType.CAMPAIGN:
        return campaignLoading;
      case NotificationType.CHARITY:
        return charityLoading;
      case NotificationType.ORPHAN:
        return orphanLoading;
      default:
        return false;
    }
  };

  // Function to fetch data based on selected type
  const fetchDataByType = async (type: number) => {
    if (!type) return;
    
    try {
      switch (type) {
        case NotificationType.CAMPAIGN:
          if (campaigns.length === 0) {
            setCampaignLoading(true);
            const campaignResponse = await EndPoints.campaign.getAllCampaigns({
              light: true,
              perPage: -1,
            });
            const campaignData = campaignResponse.data?.data;
            setCampaigns(campaignData);
            setCampaignLoading(false);
          }
          break;
          
        case NotificationType.CHARITY:
          if (charities.length === 0) {
            setCharityLoading(true);
            const charityResponse = await EndPoints.charity.getAllCharities({
              light: true,
              perPage: -1,
            });
            const charityData = charityResponse.data?.data;
            setCharities(charityData);
            setCharityLoading(false);
          }
          break;
          
        case NotificationType.ORPHAN:
          if (orphans.length === 0) {
            setOrphanLoading(true);
            const orphanResponse = await EndPoints.orphan.getAllOrphans({
              light: true,
              perPage: -1,
            });
            const orphanData = orphanResponse.data?.data;
            setOrphans(orphanData);
            setOrphanLoading(false);
          }
          break;
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      // Reset loading states
      setCampaignLoading(false);
      setCharityLoading(false);
      setOrphanLoading(false);
    }
  };

  // Get entity options based on type
  const getEntityOptions = (type: number) => {
    switch (type) {
      case NotificationType.CAMPAIGN:
        return campaigns?.map((campaign, index) => ({
          key: index,
          label: campaign.name,
          value: campaign.id,
        }));
      case NotificationType.CHARITY:
        return charities?.map((charity, index) => ({
          key: index,
          label: charity.name,
          value: charity.id,
        }));
      case NotificationType.ORPHAN:
        return orphans?.map((orphan, index) => ({
          key: index,
          label: `${orphan.firstName} ${orphan.lastName}`,
          value: orphan.id,
        }));
      default:
        return [];
    }
  };

  // Get entity field name based on type
  const getEntityFieldName = (type: number) => {
    // Always return item_id to match the backend validation
    return "item_id";
  };

  // Get entity label based on type
  const getEntityLabel = (type: number) => {
    switch (type) {
      case NotificationType.CAMPAIGN:
        return t("campaign");
      case NotificationType.CHARITY:
        return t("charity");
      case NotificationType.ORPHAN:
        return t("orphan");
      default:
        return "";
    }
  };

  // Fetch data when type changes
  useEffect(() => {
    if (typeValue) {
      fetchDataByType(typeValue);
    }
  }, [typeValue]);

  if (loading.includes("details")) {
    return (
      <div style={{ height: "50vh" }}>
        <Spin />
      </div>
    );
  }

  return (
    <Row gutter={[16, 8]}>
      {languages?.map((lang, index) => {
        return (
          <Col span={12} key={`title-${index}`}>
            <FieldBuilder
              key={index}
              name={`title[${lang?.code}]`}
              label={`${t("title")} (${lang?.name})`}
              input={{ type: "text" }}
              rules={{ required: true }}
            />
          </Col>
        );
      })}
      {languages?.map((lang, index) => {
        return (
          <Col span={12} key={`body-${index}`}>
            <FieldBuilder
              key={index}
              name={`body[${lang?.code}]`}
              label={`${t("body")} (${lang?.name})`}
              input={{ type: "text" }}
              rules={{ required: true }}
            />
          </Col>
        );
      })}

      <Col span={12}>
        <FieldBuilder
          name="topic"
          label={`${t("user_slide")}`}
          width="large"
          rules={{ required: true }}
          input={{
            type: "select",
            loading: false,
            options: Object.entries(TopicType).map(([key, value]) => ({
              key,
              label: t(`${value}`),
              value,
            }))
          }}
        />
      </Col>
      <Col span={12}>
        <FieldBuilder
          name="type"
          label={`${t("type")}`}
          width="large"
          rules={{ required: true }}
          input={{
            type: "select",
            loading: false,
            options: [
              { value: NotificationType.CAMPAIGN, label: t(getNotificationType(NotificationType.CAMPAIGN)) },
              { value: NotificationType.CHARITY, label: t(getNotificationType(NotificationType.CHARITY)) },
              { value: NotificationType.ORPHAN, label: t(getNotificationType(NotificationType.ORPHAN)) }
            ]
          }}
        />
      </Col>
      
      {typeValue > 0 && (
        <Col span={12}>
          <FieldBuilder
            name={getEntityFieldName(typeValue)}
            label={getEntityLabel(typeValue)}
            width="large"
            rules={{ required: true }}
            input={{
              type: "select",
              loading: getLoadingStateByType(typeValue),
              allowSearch: true,
              options: getEntityOptions(typeValue),
            }}
          />
        </Col>
      )}
      
      {/*<Col span={16} />*/}
      {/*{languages?.map((lang, index) => {*/}
      {/*  return (*/}
      {/*    <Col span={12} key={`note-${index}`}>*/}
      {/*      <FieldBuilder*/}
      {/*        key={index}*/}
      {/*        name={`note[${lang?.code}]`}*/}
      {/*        label={`${t("note")} (${lang?.name})`}*/}
      {/*        input={{ type: "text-area" }}*/}
      {/*        rules={{ required: false }}*/}
      {/*      />*/}
      {/*    </Col>*/}
      {/*  );*/}
      {/*})}*/}
    </Row>
  );
};

const Form = () => {
  const { t } = useTranslation();
  const { details, actions } = useContext(GeneralNotificationContext);
  const { id } = useParams();

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  return (
    <MainForm
      onSubmit={(data) => {
        details
          ? actions.updateGeneralNotification(details?.id, data)
          : actions.createGeneralNotification(data);
      }}
      title={
        details
          ? t("update_general_notification")
          : t("add_general_notification")
      }
      defaultValues={
        {
          title: getTranslations(details?.translations?.title),
          body: getTranslations(details?.translations?.body),
          topic: details?.topic,
          type: details?.type,
          item_id: details?.itemId
        } as IUpdateGeneralNotification
      }
    >
      <FormContent />
    </MainForm>
  );
};

const GeneralNotificationForm = () => {
  return (
    <GeneralNotificationContextProvider>
      <Form />
    </GeneralNotificationContextProvider>
  );
};

export default GeneralNotificationForm;
